
// ================================
// src/pages/SignalBotPage.tsx — FINAL SAFE SWITCH + GUIDE LINES + LONG/SHORT CONTINUOUS SEGMENTS (FIXED)
// - TF 변경 시에도 데이터 섞임 방지 (symbol+tf 조합마다 connToken++)
// - REST/WS 이전 응답은 토큰 검사로 버림 → 1m/15m 섞임 제거
// - Pro W/P/F 옵션 시 실제 차트에 S/R, Pivot, Fib 라인 표시
// - ✅ 롱/숏 “구간”을 연속 표시(Area 2개: Long/Short)
//   - 초기 로드(loadMain)에서 전체 세그먼트 setData
//   - WS 업데이트에서 마지막 bar만 update (EMA50/EMA200 실시간 갱신)
// - ✅ FIX: bootWS()가 resetSeries()로 히스토리를 지우던 문제 제거
//   - resetSeries()는 symbol/tf 변경(새 연결)에서 1회만 수행
// - 광고 로직은 기존 유지 (네이티브에서만 AdMob 동작)
// ================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createChart,
  ColorType,
  LineStyle,
  IChartApi,
  ISeriesApi,
  LogicalRange,
} from "lightweight-charts";

import {
  initAd,
  showRewardedAndWait,
  isNativeApp,
} from "../lib/ad";
import { grantPro, readProUntil, isProNow } from "../lib/proUnlock";

import {
  TF,
  Candle,
  TF_MAP,
  BINANCE,
  WS_BINANCE,
  emaSeries,
  atr,
  ema,
  supportResistance,
  classicPivot,
  fibLevels,
  volatilityRegime,
  Analysis,
  Dir,
} from "../utils/chartUtils";
// TF별 초 단위 (WS 무응답 감지용)
const TF_SEC: Record<TF, number> = {
  "1m": 60,
  "5m": 300,
  "15m": 900,
  "30m": 1800,
  "1h": 3600,
  "4h": 14400,
  "1d": 86400,
};


import BasicPanel from "../components/signal/BasicPanel";
import MidPanel from "../components/signal/MidPanel";
import ProPanel from "../components/signal/ProPanel";
import TermsPanel from "../components/signal/TermsPanel";

/* ---------- helpers ---------- */
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const lsSet = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {}
};
const debounce = (f: Function, ms: number) => {
  let t: any;
  return (...a: any[]) => {
    clearTimeout(t);
    t = setTimeout(() => f(...a), ms);
  };
};
const normalizeSym = (raw: string) => {
  const up = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!up) return "";
  return up.endsWith("USDT") ? up : `${up}USDT`;
};

const DEFAULT_SYM = "BTCUSDT";
const LS = { PREF: "signal.pref.v9", LAST_TAB: "signal.lastTab" };
const QUICK = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "XRPUSDT"];


/* ---------- EMA array utilities (for continuous long/short segments) ---------- */
function calcEmaArray(values: number[], period: number): Array<number | null> {
  if (!Array.isArray(values) || values.length === 0) return [];
  if (period <= 1) return values.map((v) => (Number.isFinite(v) ? v : null));

  const out: Array<number | null> = new Array(values.length).fill(null);
  const k = 2 / (period + 1);

  // Seed with SMA of first `period` points (typical EMA)
  let sum = 0;
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) {
      out[i] = null;
      continue;
    }

    if (count < period) {
      sum += v;
      count += 1;
      if (count === period) {
        const sma = sum / period;
        out[i] = sma;
        // subsequent points use EMA formula
        for (let j = i + 1; j < values.length; j++) {
          const x = values[j];
          if (!Number.isFinite(x)) {
            out[j] = null;
            continue;
          }
          const prev = out[j - 1];
          // prev can be null if a gap exists; restart seed from this point
          if (prev == null) {
            out[j] = x;
          } else {
            out[j] = x * k + prev * (1 - k);
          }
        }
        break;
      } else {
        out[i] = null;
      }
    }
  }

  // If we never reached period (short history), fallback: simple rolling EMA from first point
  if (out.every((x) => x == null)) {
    let prev: number | null = null;
    for (let i = 0; i < values.length; i++) {
      const v = values[i];
      if (!Number.isFinite(v)) {
        out[i] = null;
        continue;
      }
      if (prev == null) {
        prev = v;
        out[i] = v;
      } else {
        prev = v * k + prev * (1 - k);
        out[i] = prev;
      }
    }
  }

  return out;
}

function buildDirSegments(
  cs: Candle[],
  ema50Arr: Array<number | null>,
  ema200Arr: Array<number | null>
) {
  const longData: Array<{ time: number; value: number | null }> = [];
  const shortData: Array<{ time: number; value: number | null }> = [];

  for (let i = 0; i < cs.length; i++) {
    const t = cs[i].time;
    const e50 = ema50Arr[i];
    const e200 = ema200Arr[i];

    if (e50 == null || e200 == null) {
      longData.push({ time: t, value: null });
      shortData.push({ time: t, value: null });
      continue;
    }

    const isLong = e50 >= e200;
    longData.push({ time: t, value: isLong ? 1 : null });
    shortData.push({ time: t, value: isLong ? null : 1 });
  }

  return { longData, shortData };
}

/* ================================================ */
const SignalBotPage: React.FC = () => {
  const { t } = useTranslation(["signal", "common"]);
  const isAppUA = isNativeApp();
  /* ---------- Pro state (single source of truth) ---------- */
  const [proUntil, setProUntil] = useState<number>(() => readProUntil());
  const [tick, setTick] = useState<number>(Date.now());

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const hasPro = useMemo(() => {
    if (!isAppUA) return true; // 웹은 항상 Pro
    return isProNow();
  }, [isAppUA, proUntil, tick]);

  const remain = Math.max(0, Math.floor((proUntil - tick) / 1000));
  const mm = String(Math.floor(remain / 60)).padStart(2, "0");
  const ss = String(remain % 60).padStart(2, "0");

  const [tab, _setTab] = useState<"basic" | "mid" | "pro" | "terms">(
    () => (lsGet(LS.LAST_TAB) as any) || "basic"
  );
  const setTab = (v: any) => {
    _setTab(v);
    lsSet(LS.LAST_TAB, v);
  };

  /* ---------- symbol / tf ---------- */
  const pref = useMemo(() => {
    try {
      return JSON.parse(lsGet(LS.PREF) || "{}");
    } catch {
      return {};
    }
  }, []);
  const [symbol, setSymbol] = useState<string>(pref.lastSymbol || DEFAULT_SYM);
  const [tf, setTf] = useState<TF>(pref.lastTF || "15m");
  const [optSR, setOptSR] = useState<boolean>(pref?.opts?.sr ?? true);
  const [optWalls, setOptWalls] = useState<boolean>(pref?.opts?.walls ?? false);
  const [optPivot, setOptPivot] = useState<boolean>(pref?.opts?.pivot ?? false);
  const [optFib, setOptFib] = useState<boolean>(pref?.opts?.fib ?? false);
  const [yLock, setYLock] = useState<boolean>(false);

  useEffect(() => {
    lsSet(
      LS.PREF,
      JSON.stringify({
        lastSymbol: symbol,
        lastTF: tf,
        opts: { sr: optSR, walls: optWalls, pivot: optPivot, fib: optFib },
      })
    );
  }, [symbol, tf, optSR, optWalls, optPivot, optFib]);

  /* ---------- anti-mix / lifecycle guards ---------- */
  const currentSymRef = useRef<string>(symbol);
  const connTokenRef = useRef<number>(0); // symbol+tf 조합마다 증가
  const ignoreUntilRef = useRef<number>(0); // 보호구간 (ms)
  const pendingFetchRef = useRef<AbortController | null>(null);
  const pendingTickerRef = useRef<AbortController | null>(null); // 24h ticker fetch

  // dispose/타이머/부팅 가드
  const disposedRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const isBootingRef = useRef(false);
  const isLoadingRef = useRef(false);
  const wsRebootTimerRef = useRef<number | null>(null);

  /* ---------- chart refs ---------- */
  const chartEl = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const csRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const ema50Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const ema200Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const priceLineRef = useRef<any>(null);
  const guideLinesRef = useRef<any[]>([]); // W / P / F 라인 저장
  const tradeMarkersRef = useRef<any[]>([]);
  // ✅ Long/Short continuous segments (Area series)
  const longAreaRef = useRef<ISeriesApi<"Area"> | null>(null);
  const shortAreaRef = useRef<ISeriesApi<"Area"> | null>(null);

  // ✅ EMA runtime state for WS updates (for long/short area update)
  const emaStateRef = useRef<{
    period50: number;
    period200: number;
    k50: number;
    k200: number;
    ema50: number | null;
    ema200: number | null;
  }>({
    period50: 50,
    period200: 200,
    k50: 2 / (50 + 1),
    k200: 2 / (200 + 1),
    ema50: null,
    ema200: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const fittedRef = useRef(false);
  const lastTickAt = useRef(0);
  const chartReadyRef = useRef(false);
  const [price, setPrice] = useState<number | null>(null);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [pct24h, setPct24h] = useState<number | null>(null); // Binance 24h change %

  /* ---------- Binance 전체 심볼 로드 (검색용) ---------- */
  const [allSymbols, setAllSymbols] = useState<string[]>(QUICK);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BINANCE}/api/v3/exchangeInfo`);
        const j = await r.json();
        const syms = Array.isArray(j?.symbols)
          ? j.symbols
              .filter((x: any) => x?.status === "TRADING" && /USDT$/.test(x?.symbol))
              .map((x: any) => x.symbol)
          : [];
        if (alive && syms.length) setAllSymbols(syms.sort());
      } catch {
        /* fallback QUICK 유지 */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 드롭다운 표시 제어
  const [showHints, setShowHints] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const onDocPointerDown = (e: PointerEvent) => {
      if (!searchBoxRef.current?.contains(e.target as Node)) {
        setShowHints(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, []);

  /* ---------- 검색창 ---------- */
  const [querySym, setQuerySym] = useState<string>("");
  const filteredSyms = useMemo(() => {
    const q = (querySym || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const pool = allSymbols?.length ? allSymbols : QUICK;
    if (!q) return pool.slice(0, 50);
    const starts = pool.filter((s) => s.startsWith(q) || s.startsWith(`${q}USDT`));
    const contains = pool.filter((s) => !starts.includes(s) && s.includes(q));
    return [...starts, ...contains].slice(0, 50);
  }, [querySym, allSymbols]);

  /* ---------- Chart init (dispose-safe) ---------- */
  useEffect(() => {
    if (!chartEl.current) return;
    disposedRef.current = false;

    const el = chartEl.current;
    const chart = createChart(el, {
      width: el.clientWidth,
      height: Math.max(260, el.clientHeight || 360),
      layout: { background: { type: ColorType.Solid, color: "#0b0f16" }, textColor: "#cbd5e1" },
      grid: { vertLines: { color: "#16202b" }, horzLines: { color: "#16202b" } },
      rightPriceScale: {
  autoScale: true,
  entireTextOnly: true,
  scaleMargins: {
    top: 0.12,
    bottom: 0.18,
  },
  borderVisible: false,
},

      timeScale: { borderVisible: false, timeVisible: true, rightOffset: 2, barSpacing: 6 },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: false },
    });
    chartRef.current = chart;
    chartReadyRef.current = true;

    csRef.current = chart.addCandlestickSeries({
      upColor: "#00ff00",
      downColor: "#ff0000",
      borderUpColor: "#00ff00",
      borderDownColor: "#ff0000",
      wickUpColor: "#00ff00",
      wickDownColor: "#ff0000",
    });
    volRef.current = chart.addHistogramSeries({ priceFormat: { type: "volume" }, priceScaleId: "vol" });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.8, bottom: 0.02 } });
    ema50Ref.current = chart.addLineSeries({ lineWidth: 2, color: "#3b82f6" });
    ema200Ref.current = chart.addLineSeries({ lineWidth: 2, color: "#94a3b8", lineStyle: LineStyle.Dotted });

// === Direction (Long / Short) scale ===


longAreaRef.current = chart.addAreaSeries({
  priceScaleId: "dir",
  lineWidth: 0,
  topColor: "rgba(34,197,94,0.12)",
  bottomColor: "rgba(34,197,94,0.01)",
  priceLineVisible: false,
  lastValueVisible: false,
});

shortAreaRef.current = chart.addAreaSeries({
  priceScaleId: "dir",
  lineWidth: 0,
  topColor: "rgba(239,68,68,0.12)",
  bottomColor: "rgba(239,68,68,0.01)",
  priceLineVisible: false,
  lastValueVisible: false,
});

    const ro = new ResizeObserver(
      debounce(() => {
        if (disposedRef.current || !chartRef.current || !chartEl.current) return;
        chart.applyOptions({ width: el.clientWidth, height: Math.max(260, el.clientHeight || 360) });
      }, 150)
    );
    resizeObsRef.current = ro;
    ro.observe(el);

    return () => {
      // 모든 비동기/옵저버/소켓/차트 정리
      disposedRef.current = true;

      try {
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      } catch {}
      rafIdRef.current = null;
      
      try {
        if (wsRebootTimerRef.current) clearTimeout(wsRebootTimerRef.current);
      } catch {}
      wsRebootTimerRef.current = null;

      try {
        pendingFetchRef.current?.abort();
      } catch {}
      pendingFetchRef.current = null;
      try {
        pendingTickerRef.current?.abort();
      } catch {}
      pendingTickerRef.current = null;
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;

      try {
        resizeObsRef.current?.disconnect();
      } catch {}
      resizeObsRef.current = null;

      // 가이드 라인 제거
      try {
        if (csRef.current && guideLinesRef.current.length) {
          for (const ln of guideLinesRef.current) {
            try {
              csRef.current.removePriceLine(ln);
            } catch {}
          }
        }
      } catch {}

      try {
        chartRef.current?.remove();
      } catch {}
      chartRef.current = null;
      csRef.current = null;
      volRef.current = null;
      ema50Ref.current = null;
      ema200Ref.current = null;
      longAreaRef.current = null;
      shortAreaRef.current = null;

      priceLineRef.current = null;
      guideLinesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSeries = useCallback(() => {
  if (disposedRef.current) return;

  for (const ref of [csRef, volRef, ema50Ref, ema200Ref, longAreaRef, shortAreaRef]) {
    try {
      ref.current?.setData([] as any);
    } catch {}
  }

  if (priceLineRef.current) {
    try {
      csRef.current?.removePriceLine(priceLineRef.current);
    } catch {}
    priceLineRef.current = null;
  }

  if (csRef.current && guideLinesRef.current.length) {
    for (const ln of guideLinesRef.current) {
      try {
        csRef.current.removePriceLine(ln);
      } catch {}
    }
  }
  guideLinesRef.current = [];

  // 🔥 BUY / SELL 마커 초기화 (필수)
  tradeMarkersRef.current = [];
  csRef.current?.setMarkers([]);
}, []);


// 단일화된 fitOnce 함수
const fitOnce = useCallback(() => {
  if (disposedRef.current || !chartRef.current) return;

  const chart = chartRef.current;

  requestAnimationFrame(() => {
    chart.timeScale().fitContent();

    chart.applyOptions({
      rightPriceScale: {
        autoScale: true,
        scaleMargins: { top: 0.12, bottom: 0.18 },
      },
    });

    // 🔥 Y축 강제 리셋
    chart.priceScale("right").applyOptions({
      autoScale: true,
    });
  });
}, []);


// Y-LOCK 버튼 동작 연결
useEffect(() => {
  if (chartRef.current) {
    chartRef.current.applyOptions({
      rightPriceScale: { 
        autoScale: !yLock // 락이 걸리면 자동 스케일링 해제
      }
    });
  }
}, [yLock]);
  /* ---------- 줌 리밋 & 자동 중앙 보정 ---------- */
  const enforceZoomLimit = useCallback(() => {
    if (disposedRef.current) return;
    const chart = chartRef.current;
    if (!chart) return;
    const range = chart.timeScale().getVisibleLogicalRange() as LogicalRange;
    if (!range) return;
    const width = Number(range.to) - Number(range.from);
    if (width < 30 || width > 2000) chart.timeScale().fitContent();
  }, []);

  /* ---------- 심볼 스위치 (완전 안전) ---------- */
  const switchSymbol = useCallback((raw: string) => {
    const next = normalizeSym(raw);
    if (!next || currentSymRef.current === next) return;

    // 토큰 증가/WS/요청 정리는 symbol+tf useEffect에서 공통 처리
    ignoreUntilRef.current = Date.now() + 1200;

    try {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    } catch {}
    rafIdRef.current = null;
    try {
      pendingFetchRef.current?.abort();
    } catch {}
    pendingFetchRef.current = null;
    try {
      pendingTickerRef.current?.abort();
    } catch {}
    pendingTickerRef.current = null;
    try {
      wsRef.current?.close();
    } catch {}

    currentSymRef.current = next;
    setSymbol(next);
    setPct24h(null);
    fittedRef.current = false;

    // ✅ EMA state reset (so WS updates won't use stale EMA)
    emaStateRef.current.ema50 = null;
    emaStateRef.current.ema200 = null;
  }, []);

  /* ---------- load main (REST 백필, 토큰 검증 + Abort + 락) ---------- */
  const loadMain = useCallback(async () => {
  if (!chartReadyRef.current) return;   // 🔥 추가
  if (disposedRef.current || isLoadingRef.current) return;
  isLoadingRef.current = true;


    const myToken = connTokenRef.current;
    setLoadErr(null);

    // 보호구간 갱신
    ignoreUntilRef.current = Date.now() + 1200;

    // 진행중 요청 취소
    try {
      pendingFetchRef.current?.abort();
    } catch {}
    const ctrl = new AbortController();
    pendingFetchRef.current = ctrl;

    try {
      const tfKey = TF_MAP[tf] || "1m";
      const r = await fetch(
        `${BINANCE}/api/v3/klines?symbol=${currentSymRef.current}&interval=${tfKey}&limit=1000`,
        { signal: ctrl.signal }
      );
      const arr = await r.json();

      if (disposedRef.current || connTokenRef.current !== myToken) return;

      const cs: Candle[] = (Array.isArray(arr) ? arr : []).map((x: any) => ({
        time: x[0] / 1000,
        open: +x[1],
        high: +x[2],
        low: +x[3],
        close: +x[4],
        volume: +x[5],
      }));

      csRef.current?.setData(cs as any);
      volRef.current?.setData(
        cs.map((c) => ({
          time: c.time,
          value: c.volume,
          color: c.close >= c.open ? "#0f06" : "#f006",
        })) as any
      );

     // EMA lines
ema50Ref.current?.setData(emaSeries(cs.map((c) => c.close), 50, cs.map((c) => c.time)) as any);
ema200Ref.current?.setData(emaSeries(cs.map((c) => c.close), 200, cs.map((c) => c.time)) as any);

// ===============================
// 🔥 EMA 배열 계산 (공통 사용)
// ===============================
const closes = cs.map((c) => c.close);
const ema50Arr = calcEmaArray(closes, 50);
const ema200Arr = calcEmaArray(closes, 200);

// ===============================
// 🔥 Long / Short 연속 영역
// ===============================
try {
  const { longData, shortData } = buildDirSegments(cs, ema50Arr, ema200Arr);
  longAreaRef.current?.setData(longData as any);
  shortAreaRef.current?.setData(shortData as any);
} catch {}


// ===============================
// 🔥 EMA state seed (WS 이어받기)
// ===============================
emaStateRef.current.ema50 = null;
emaStateRef.current.ema200 = null;
for (let i = ema50Arr.length - 1; i >= 0; i--) {
  if (emaStateRef.current.ema50 == null && ema50Arr[i] != null) {
    emaStateRef.current.ema50 = ema50Arr[i] as number;
  }
  if (emaStateRef.current.ema200 == null && ema200Arr[i] != null) {
    emaStateRef.current.ema200 = ema200Arr[i] as number;
  }
  if (emaStateRef.current.ema50 != null && emaStateRef.current.ema200 != null) break;
}


      const last = cs.at(-1)?.close ?? null;
      setPrice(last);
      setCandles(cs);
      // 🔥 최초 진입 분석 강제 계산 (기초분석 멈춤 버그 완전 제거)
try {
  if (cs.length && last != null) {
    const closes = cs.map((c) => c.close);
    const _ema50 = ema(closes, 50);
    const _ema200 = ema(closes, 200);
    const A = atr(cs, 14);
    const { sup, res } = supportResistance(cs, 60);

    const dir: Dir =
      last > _ema50 && last > _ema200
        ? "long"
        : last < _ema50 && last < _ema200
        ? "short"
        : "wait";

    const piv = classicPivot(cs);
    const fib = fibLevels(cs, 200);
    const volReg = volatilityRegime(last, A);

    setAnalysis({
      ema50: _ema50,
      ema200: _ema200,
      atr: A,
      sup,
      res,
      dir,
      piv,
      fib,
      volReg,
      score: 0,
      rr1: 0,
      rr2: 0,
      plan: null,
      cntS: 0,
      cntR: 0,
      regime: "neutral",
    });
  }
} catch {}


      if (priceLineRef.current) csRef.current?.removePriceLine(priceLineRef.current);
      if (last) priceLineRef.current = csRef.current?.createPriceLine({ price: last, lineWidth: 1, title: "LAST" });

      fitOnce();
    } catch (e: any) {
      if (e?.name !== "AbortError") setLoadErr(e?.message || "LOAD_ERROR");
    } finally {
      if (pendingFetchRef.current === ctrl) pendingFetchRef.current = null;
      isLoadingRef.current = false;
    }
  }, [tf]);

  /* ---------- 24h Ticker (Binance 공식 등락률) ---------- */
  const loadTicker24h = useCallback(async () => {
    const myToken = connTokenRef.current;
    try {
      pendingTickerRef.current?.abort();
    } catch {}
    const ctrl = new AbortController();
    pendingTickerRef.current = ctrl;
    try {
      const r = await fetch(`${BINANCE}/api/v3/ticker/24hr?symbol=${currentSymRef.current}`, { signal: ctrl.signal });
      const j = await r.json();
      if (disposedRef.current || connTokenRef.current !== myToken) return;
      const pct = Number(j?.priceChangePercent);
      setPct24h(Number.isFinite(pct) ? pct : null);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
    } finally {
      if (pendingTickerRef.current === ctrl) pendingTickerRef.current = null;
    }
  }, []);

  // 심볼 변경 시 & 주기적으로 24h 등락률 갱신
  useEffect(() => {
    loadTicker24h();
    const id = setInterval(loadTicker24h, 10000);
    return () => clearInterval(id);
  }, [symbol, loadTicker24h]);

  /* ---------- WS (토큰 검증 + 보호구간 + 중복부팅 방지 + rAF 업데이트) ---------- */
  const bootWS = useCallback(() => {
    if (disposedRef.current || isBootingRef.current) return;
    isBootingRef.current = true;

    const myToken = connTokenRef.current;

    try {
      wsRef.current?.close();
    } catch {}

    // ✅ FIX: 여기서 resetSeries() 하면 loadMain으로 세팅한 히스토리가 지워짐
    // resetSeries()는 "연결 갱신(useEffect symbol/tf)"에서 1회만 수행
    fittedRef.current = false;

    const tfKey = TF_MAP[tf] || "1m";
    const ws = new WebSocket(`${WS_BINANCE}/${currentSymRef.current.toLowerCase()}@kline_${tfKey}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (connTokenRef.current === myToken) isBootingRef.current = false;
    };

    ws.onmessage = (ev) => {
      if (disposedRef.current || connTokenRef.current !== myToken) return;
      if (Date.now() < ignoreUntilRef.current) return;

      try {
        const j = JSON.parse(ev.data);
        const k = j?.k;
        if (!k) return;

        const bar = { time: k.t / 1000, open: +k.o, high: +k.h, low: +k.l, close: +k.c };

        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => {
          if (disposedRef.current || connTokenRef.current !== myToken) return;

          csRef.current?.update(bar as any);
          volRef.current?.update({
            time: bar.time,
            value: +k.v,
            color: +k.c >= +k.o ? "#0f06" : "#f006",
          } as any);

          setPrice(+k.c);

          // ✅ Update EMA state incrementally & update long/short area (continuous segments)
          try {
            const st = emaStateRef.current;
            const close = +k.c;

            // If EMA not seeded yet, seed with close (safe fallback)
            if (st.ema50 == null || !Number.isFinite(st.ema50)) st.ema50 = close;
            if (st.ema200 == null || !Number.isFinite(st.ema200)) st.ema200 = close;

            st.ema50 = close * st.k50 + (st.ema50 as number) * (1 - st.k50);
            st.ema200 = close * st.k200 + (st.ema200 as number) * (1 - st.k200);

            // ✅ EMA 라인도 실시간 갱신
            ema50Ref.current?.update({ time: bar.time, value: st.ema50 as number } as any);
            ema200Ref.current?.update({ time: bar.time, value: st.ema200 as number } as any);

            const isLongNow = (st.ema50 as number) >= (st.ema200 as number);

/* ===========================
   🔥 BUY / SELL 마커 (정상 위치)
   =========================== */

// 봉 확정 시에만 신호 생성
if (k.x === true) {
  const lastSide =
    tradeMarkersRef.current.length > 0
      ? tradeMarkersRef.current[tradeMarkersRef.current.length - 1].side
      : null;

  const curSide = isLongNow ? "long" : "short";

  if (lastSide !== curSide) {
    tradeMarkersRef.current.push({
      time: bar.time,
      position: isLongNow ? "belowBar" : "aboveBar",
      color: isLongNow ? "#22c55e" : "#ef4444",
      shape: isLongNow ? "arrowUp" : "arrowDown",
      text: isLongNow ? "BUY" : "SELL",
      side: curSide,
    });

    csRef?.current?.setMarkers(tradeMarkersRef.current);
  }
}

/* ===========================
   🔁 Long / Short 영역 (기존 유지)
   =========================== */

longAreaRef.current?.update({
  time: bar.time,
  value: isLongNow ? 1 : null,
});

shortAreaRef.current?.update({
  time: bar.time,
  value: isLongNow ? null : 1,
});


          } catch {}

          if (!fittedRef.current) fitOnce();
          lastTickAt.current = Date.now();
          enforceZoomLimit();
        });
      } catch {}
    };

    ws.onclose = () => {
      if (disposedRef.current || connTokenRef.current !== myToken) return;
      // 단일 재부팅 타이머(2초 백오프)
      if (wsRebootTimerRef.current) return;
      wsRebootTimerRef.current = window.setTimeout(() => {
        wsRebootTimerRef.current = null;
        loadMain();
        bootWS();
      }, 2000) as any;
    };
  }, [tf, loadMain, enforceZoomLimit]);

  // ✅ symbol 또는 tf 가 바뀔 때마다 새 토큰 할당 → 이전 TF/심볼 데이터는 전부 무시
  useEffect(() => {
    connTokenRef.current++;
    ignoreUntilRef.current = Date.now() + 1200;

    currentSymRef.current = symbol; // 상태 동기화

    // ✅ 새 연결 시작: 히스토리/라인/영역 초기화는 여기서 1회만
    resetSeries();
    fittedRef.current = false;
    lastTickAt.current = 0;

    // ✅ EMA state reset on conn
    emaStateRef.current.ema50 = null;
    emaStateRef.current.ema200 = null;

    const myToken = connTokenRef.current;

    // ✅ loadMain 완료 후 WS 부팅 (히스토리 유지 + 마지막 bar만 update)
    (async () => {
  while (!chartReadyRef.current) {
    await new Promise(r => setTimeout(r, 50));
  }
  await new Promise(r => requestAnimationFrame(r));
  await loadMain();
  if (disposedRef.current || connTokenRef.current !== myToken) return;
  bootWS();
})();

    return () => {
      try {
        wsRef.current?.close();
      } catch {}
    };
  }, [symbol, tf, loadMain, bootWS, resetSeries]);

  /* ---------- no-tick 감지로 TF별 주기적 백필(완화형) ---------- */
  useEffect(() => {
    const sec = TF_SEC[tf] ?? 60;
    const NO_TICK_MS = Math.max(45000, sec * 3000); // TF의 3배(최소 45s)
    const id = setInterval(() => {
      if (disposedRef.current || isLoadingRef.current) return;
      if (Date.now() - lastTickAt.current > NO_TICK_MS) loadMain();
    }, 7000);
    return () => clearInterval(id);
  }, [tf, loadMain]);


  /* ---------- Ads init ---------- */
  useEffect(() => {
  initAd().catch(() => {});
}, []);


const unlockProByReward = async () => {
  if (isAppUA) {
    const result = await showRewardedAndWait();

    // 정상 리워드
    if (result === "rewarded") {
      const until = grantPro(3600000);
      setProUntil(until);
      localStorage.setItem("signal.proUntil", String(until));
      return "success";
    }

    // 광고 서버 문제 / 준비 실패 → 무료 패스
    if (result === "failed") {
  alert("Lucky! Free Pass (Ad Server Busy)");
      const until = grantPro(3600000);
      setProUntil(until);
      localStorage.setItem("signal.proUntil", String(until));
      return "success";
    }

    // 사용자가 닫음
    return "closed";
  }

  // 웹 → 광고 없이 바로 Pro
  const until = grantPro(3600000);
  setProUntil(until);
  return "success";
};



const onClickPro = async () => {
  if (hasPro) {
    setTab("pro");
    return;
  }

  // 럭키 당첨 혹은 광고 시청 완료 시 success 반환
  const res = await unlockProByReward();
  if (res === "success") {
    setTab("pro");
  }
};


  /* ---------- analysis ---------- */
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  useEffect(() => {
    if (!candles.length || price == null) return;
    const closes = candles.map((c) => c.close);
    const _ema50 = ema(closes, 50);
    const _ema200 = ema(closes, 200);
    const A = atr(candles, 14);
    const { sup, res } = supportResistance(candles, 60);
    const dir: Dir =
      price > _ema50 && price > _ema200
        ? "long"
        : price < _ema50 && price < _ema200
        ? "short"
        : "wait";
    const piv = classicPivot(candles);
    const fib = fibLevels(candles, 200);
    const volReg = volatilityRegime(price, A);
    setAnalysis({
      ema50: _ema50,
      ema200: _ema200,
      atr: A,
      sup,
      res,
      dir,
      piv,
      fib,
      volReg,
      score: 0,
      rr1: 0,
      rr2: 0,
      plan: null,
      cntS: 0,
      cntR: 0,
      regime: "neutral",
    });
  }, [candles, price]);

  /* ---------- Guide Lines: W / P / F ---------- */
  const applyGuideLines = useCallback(() => {
    if (disposedRef.current) return;
    const series = csRef.current;
    if (!series) return;

    // 기존 라인 제거
    if (guideLinesRef.current.length) {
      for (const ln of guideLinesRef.current) {
        try {
          series.removePriceLine(ln);
        } catch {}
      }
    }
    guideLinesRef.current = [];

    if (!analysis) return;

    // SR / Walls → S/R 라인
    if ((optSR || optWalls) && typeof analysis.sup === "number" && typeof analysis.res === "number") {
      if (Number.isFinite(analysis.sup)) {
        const ln = series.createPriceLine({
          price: analysis.sup,
          color: "#22c55e",
          lineStyle: LineStyle.Dotted,
          lineWidth: 1,
          title: "S",
        });
        guideLinesRef.current.push(ln);
      }
      if (Number.isFinite(analysis.res)) {
        const ln = series.createPriceLine({
          price: analysis.res,
          color: "#ef4444",
          lineStyle: LineStyle.Dotted,
          lineWidth: 1,
          title: "R",
        });
        guideLinesRef.current.push(ln);
      }
    }

    // Pivot (P / R1.. / S1..)
    if (optPivot && (analysis as any).piv) {
      const piv: any = (analysis as any).piv;
      const keys = ["p", "r1", "r2", "r3", "s1", "s2", "s3"];
      for (const k of keys) {
        const v = piv?.[k];
        if (typeof v === "number" && Number.isFinite(v)) {
          const ln = series.createPriceLine({
            price: v,
            color: "#eab308",
            lineStyle: LineStyle.Solid,
            lineWidth: 1,
            title: k.toUpperCase(), // P / R1 / S1 ...
          });
          guideLinesRef.current.push(ln);
        }
      }
    }

    // Fib 레벨 (F)
    if (optFib && (analysis as any).fib) {
      const fib: any = (analysis as any).fib;
      const lvls: any[] = Array.isArray(fib?.levels)
        ? fib.levels
        : Array.isArray(fib)
        ? fib
        : [];
      lvls.forEach((lv, idx) => {
        const p = typeof lv === "number" ? lv : lv?.price;
        if (typeof p === "number" && Number.isFinite(p)) {
          const ln = series.createPriceLine({
            price: p,
            color: "#38bdf8",
            lineStyle: LineStyle.Dotted,
            lineWidth: 1,
            title: `F${idx}`,
          });
          guideLinesRef.current.push(ln);
        }
      });
    }
  }, [analysis, optSR, optWalls, optPivot, optFib]);

  // 분석/옵션이 바뀔 때마다 자동으로 가이드라인 갱신
  useEffect(() => {
    applyGuideLines();
  }, [applyGuideLines]);

  /* ---------- UI ---------- */
  return (
    <>
      <div className="sb-root with-topbar-gap">
        <div className="container">
          {/* === 상단 심볼/가격/등락 === */}
          <div className="symbol-header compact">
            <span className="dot live" />
            <b>{symbol}</b>
            <span className="px">{price ? price.toLocaleString() : "--"}</span>
            {pct24h != null && (
              <span
                className="pct"
                style={{ color: pct24h >= 0 ? "#4ade80" : "#f87171", fontWeight: 800, marginLeft: 6 }}
                title="24h change (Binance)"
              >
                {pct24h >= 0 ? "+" : ""}
                {pct24h.toFixed(2)}%
              </span>
            )}
          </div>

          {/* === 심볼 검색창 === */}
          <div className="symbol-search" ref={searchBoxRef}>
            <input
              type="text"
              value={querySym}
              onChange={(e) => {
                setQuerySym(e.target.value);
                setShowHints(true);
              }}
              onFocus={() => setShowHints(true)}
              onClick={() => setShowHints(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  switchSymbol(querySym);
                  setShowHints(false);
                }
              }}
              placeholder="Search symbol (e.g. BTC, ETH, SOL, XRP or BTCUSDT)"
              spellCheck={false}
              autoCapitalize="characters"
            />
            {showHints && (
              <div className="symbol-hints">
                {(filteredSyms.length ? filteredSyms : QUICK).map((sym) => (
                  <button
                    key={`hint-${sym}`}
                    className="hint"
                    onClick={() => {
                      switchSymbol(sym);
                      setShowHints(false);
                    }}
                    type="button"
                  >
                    {sym}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* === 빠른 전환 버튼 + Y-LOCK === */}
          <div className="symbol-switch">
            {QUICK.map((sym) => (
              <button
                key={sym}
                className={`symb ${symbol === sym ? "on" : ""}`}
                onClick={() => switchSymbol(sym)}
              >
                {sym.replace("USDT", "")}
              </button>
            ))}
            <button
              className={`ylock ${yLock ? "on" : ""}`}
              onClick={() => {
                setYLock((prev) => {
                  const next = !prev;
                  if (!disposedRef.current) {
                    chartRef.current?.applyOptions({
  rightPriceScale: {
    autoScale: !next,
    scaleMargins: { top: 0.12, bottom: 0.18 },
  },
  handleScroll: {
    mouseWheel: true,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: false,
  },
  handleScale: {
    mouseWheel: true,
    pinch: true,
    axisPressedMouseMove: false,
  },
});

                  }
                  return next;
                });
              }}
            >
              {yLock ? "Y-LOCK 🔒" : "Y-LOCK 🔓"}
            </button>
          </div>

          {/* === 차트 === */}
          <div className="chart-wrap">
            <div ref={chartEl} className="chart" />
          </div>

          {/* === 타임프레임 === */}
          <div className="tfsmall">
            {(["1m", "5m", "15m", "30m", "1h", "4h", "1d"] as TF[]).map((k) => (
              <button key={k} className={`tfc ${tf === k ? "on" : ""}`} onClick={() => setTf(k)}>
                {k}
              </button>
            ))}
            <button onClick={() => loadMain()}>Refresh</button>
          </div>

          {/* === 탭 === */}
          <div className="tabs">
            <button className={tab === "basic" ? "on" : ""} onClick={() => setTab("basic")}>
              {t("tab.basic", "Basic")}
            </button>
            <button className={tab === "mid" ? "on" : ""} onClick={() => setTab("mid")}>
              {t("tab.mid", "Intermediate")}
            </button>
            <button className={tab === "pro" ? "on" : ""} onClick={onClickPro}>
              {t("tab.pro", "Advanced")}
              {!hasPro ? " 🔒" : ""}
            </button>
            <button className={tab === "terms" ? "on" : ""} onClick={() => setTab("terms")}>
              {t("tab.terms", "Glossary")}
            </button>
          </div>

          {/* === Pro 공지 === */}
          <div className={`pro-notice ${hasPro ? "ok" : ""}`}>
            {hasPro ? (
              <>
                <strong>Advanced unlocked</strong> • Remaining <b>{mm}:{ss}</b>
              </>
            ) : (
              <>
                <strong>Advanced analysis</strong> — Watch ad to unlock <b>1 hour</b>.{" "}
                <button className="mini" onClick={onClickPro}>
                  Unlock 1h
                </button>
              </>
            )}
          </div>

          {/* === 패널(컴팩트 카드) === */}
          <div className="panel">
            <div className="panel-card">
              {tab === "basic" && <BasicPanel symbol={symbol} tf={tf} analysis={analysis} loadErr={loadErr} />}
            </div>
            <div className="panel-card">{tab === "mid" && <MidPanel analysis={analysis} price={price} />}</div>
            <div className="panel-card">
              {tab === "pro" && (
                <ProPanel
  symbol={symbol}
  hasPro={hasPro}
  analysis={analysis}
  candles={candles}
  onUnlockByAd={unlockProByReward}   // 🔥 실제 광고 → Pro 해제 연결
  opt={{ sr: optSR, walls: optWalls, pivot: optPivot, fib: optFib }}
                  setOpt={(n: any) => {
                    if (n.sr != null) setOptSR(n.sr);
                    if (n.walls != null) setOptWalls(n.walls);
                    if (n.pivot != null) setOptPivot(n.pivot);
                    if (n.fib != null) setOptFib(n.fib);
                  }}
                  applyGuideLines={applyGuideLines} // ✅ 실제 구현 전달
                />
              )}
            </div>
            <div className="panel-card">{tab === "terms" && <TermsPanel />}</div>
          </div>
        </div>
      </div>

     
      <style>{`
        :root{ --panel-font:13.5px; --panel-font-sm:12.5px; }

        .sb-root{padding:12px;padding-top:env(safe-area-inset-top,0px);background:#0a0d14;color:#e5eaf3;min-height:100dvh;font-size:15px;line-height:1.45}
        .container{max-width:980px;margin:0 auto}

        .symbol-header{display:flex;align-items:center;gap:8px;background:#0b0f16;border:1px solid #1b2330;border-radius:10px;padding:8px 10px;margin:4px 0;font-weight:900}
        .dot.live{width:8px;height:8px;border-radius:999px;background:#22c55e}
        .px{font-variant-numeric:tabular-nums}

        /* === symbol search === */
        .symbol-search { margin: 6px 0 2px; position: relative; }
        .symbol-search input{
          width:100%;
          padding:9px 11px;
          border-radius:8px;
          border:1px solid #2a3344;
          background:#0f1725;
          color:#e5eaf3;
          font-weight:700;
          font-size:14px;
        }
        .symbol-hints {
          display:flex; flex-direction:column; gap:4px;
          max-height:200px; overflow-y:auto;
          background:#0f1725; border:1px solid #283243; border-radius:8px;
          margin-top:6px; padding:6px; position:absolute; width:calc(100% - 24px); z-index:20;
          inset:auto 0 auto 0;
          max-width:100%;
        }
        .symbol-hints .hint {
          border:none; border-radius:6px; padding:6px 10px;
          background:#1a2435; color:#e5eaf3; font-weight:700; font-size:14px; text-align:left;
        }
        .symbol-hints .hint:hover { background:#243049; }

        .symbol-switch{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px}
        .symbol-switch .symb{border:1px solid #283243;border-radius:999px;padding:6px 10px;background:#0f1725;color:#e5eaf3;font-weight:800}
        .symbol-switch .symb.on{outline:2px solid #3b82f6}
        .ylock{border:1px solid #3a4457;border-radius:8px;padding:6px 10px;background:#101824;color:#dce3f0;font-weight:700;margin-left:auto}
        .ylock.on{background:#1a3d1f;border-color:#3b8240;color:#c6f0c8}

        /* 차트 약간 더 작게(컴팩트) */
        .chart{height:clamp(240px,38vh,480px);border:1px solid #1b2330;border-radius:12px;background:#0b0f16;margin-top:6px}
        .chart canvas{touch-action:pan-y!important; max-width:100%}

        .tfsmall{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap}
        .tfc{border:1px solid #283243;border-radius:999px;padding:6px 12px;background:#0f1725;color:#e5eaf3;font-weight:800}
        .tfc.on{outline:2px solid #3b82f6}

        .tabs{display:flex;gap:6px;margin-top:10px}
        .tabs button{border:1px solid #2a3344;background:#0f1725;color:#dbe3ee;border-radius:999px;padding:8px 12px;font-weight:800;cursor:pointer;font-size:13px}
        .tabs button.on{outline:3px solid #ffffff22;background:#1a2340}

        .pro-notice{margin:8px 0 6px;padding:10px 12px;border-radius:10px;background:#111a2b;border:1px solid #2a3b66;color:#dbe7ff;font-weight:800;font-size:13px}
        .pro-notice.ok{background:#0f1f12;border-color:#305a33;color:#cdeccf}
        .pro-notice .mini{margin-left:8px;border:1px solid #2a3b66;background:#142246;color:#e6efff;border-radius:8px;padding:6px 10px;font-weight:800;cursor:pointer}

        /* === 패널 공통 카드(초급/중급/고급/용어) — 컴팩트 + 넘침 방지 === */
        .panel{display:grid; gap:8px; margin-top:8px}
        .panel-card{
          border:1px solid #1b2330; background:#0b0f16; border-radius:12px;
          padding:10px 12px; overflow:hidden;
          font-size:var(--panel-font); line-height:1.38;
        }
        .panel-card:empty { display:none; }

        .panel-card *{ box-sizing:border-box; }
        .panel-card h1{font-size:1.1rem;margin:0 0 8px;font-weight:800}
        .panel-card h2{font-size:1.0rem;margin:8px 0 6px;font-weight:800}
        .panel-card h3{font-size:0.95rem;margin:6px 0 4px;font-weight:800}
        .panel-card p, .panel-card li, .panel-card dd, .panel-card dt{font-size:var(--panel-font); line-height:1.4; margin:4px 0}
        .panel-card small{font-size:calc(var(--panel-font) - 1px)}
        .panel-card ul, .panel-card ol{padding-left:18px; margin:6px 0}
        .panel-card table{width:100%; table-layout:fixed; border-collapse:collapse; font-size:calc(var(--panel-font) - 0.5px)}
        .panel-card th, .panel-card td{border:1px solid #1f2a3a; padding:6px 8px; word-break:break-word}
        .panel-card img, .panel-card svg, .panel-card canvas, .panel-card video{max-width:100%; height:auto; display:block}
        .panel-card code, .panel-card pre{max-width:100%; overflow:auto; font-size:12px}
        .panel-card .row{display:flex; gap:8px; flex-wrap:wrap; min-width:0}
        .panel-card .col{flex:1 1 160px; min-width:0}
        .panel-card .kv{display:flex; justify-content:space-between; gap:8px; white-space:nowrap}
        .panel-card .kv>*{min-width:0; overflow-wrap:anywhere}

        /* 작은 화면에서 더 컴팩트 */
        @media (max-width: 420px){
          .tabs button{font-size:12px; padding:7px 10px}
          .pro-notice{font-size:12.5px}
          .panel-card{font-size:var(--panel-font-sm)}
          .panel-card h1{font-size:1.0rem}
          .panel-card h2{font-size:0.95rem}
          .panel-card h3{font-size:0.9rem}
        }
      `}</style>
    </>
  );
};

export default SignalBotPage;
